// React
import React, { useState, useEffect } from "react";
import { Controller } from 'react-hook-form';
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import MediaAttribution from '../shared/MediaAttribution';
import TextField from '@mui/material/TextField';
import { styled } from "@mui/material/styles";
// custom components
import SuppCCArray from "../shared/SuppCCArray";
import SuppEmentorArray from './components/SuppEmentorArray';
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import ImageAttribution from '../shared/ImageAttribution';
import SuppMCLureArray from '../shared/SuppMCLureArray';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const PESupplementalContent = (props) => {
  const { control, formState, watch, setValue } = props.methods;
  const { errors } = formState;
  const { inputDir } = props;
  const [fileError, setFileError] = useState(null);

  const recreateSeconds = (second) => {
    if (second.includes(",")) {
      const split = second.split(",");
      return parseFloat(split[0] + "." + split[1]);
    }

    return second;
  };

  /**
   *
   * @param {string} time
   * @param {'hour' | 'minute' | 'second'} timePeriod
   * @returns {number | null}
   */
  const separateTimeStamp = (time, timePeriod) => {
    if (typeof time !== "string" || !time.includes(":")) {
      console.warn("Invalid time format. Expected HH:MM:SS format.");
      return null;
    }
    const timeArray = time.split(":");

    switch (timePeriod) {
      case "hour":
        return parseInt(timeArray[0]);
      case "minute":
        return parseInt(timeArray[1]);
      case "second":
        return recreateSeconds(timeArray[2]);
      default:
        return null;
    }
  };

  const validateFile = (file) => {
    if (file.type !== "application/json" && !file.name.includes(".srt")) {
      setFileError("Invalid file type. Please upload a JSON or SRT file.");
      return false;
    }
    return true;
  };

  const readSRT = (data) => {
    const blocksArray = data.split("\r\n\r\n");
    // clean up the array by removing empty strings
    const filteredBlocks = blocksArray.filter((block) => block.trim() !== "");

    const ccArray = filteredBlocks.map((block, i) => {
      try {
        const blockArray = block.split("\r\n");
        // if the blockArray has less than 2 items, it's not a valid block
        if (blockArray.length < 2) {
          return null;
        }

        // const index = blockArray[0];
        const timestamps = blockArray[1].split(" --> ");
        // if the timestamps array has more than 2 items, it's not a valid block
        if (timestamps.length !== 2) {
          return null;
        }

        const start_minute = separateTimeStamp(timestamps[0], "minute");
        const start_second = separateTimeStamp(timestamps[0], "second");
        const end_minute = separateTimeStamp(timestamps[1], "minute");
        const end_second = separateTimeStamp(timestamps[1], "second");
        const tl_text = blockArray.slice(2).join(" ");

        return { start_minute, start_second, end_minute, end_second, tl_text };
      } catch (err) {
        return null;
      }
    });
    // one last cleanup to remove any null items
    const validCCArray = ccArray.filter((item) => item !== null);

    setValue("content.core.cc", validCCArray);
  };

  const readJSON = (result) => {
    const data = JSON.parse(result);
    const ccArray = data.map((ccItem) => {
      const start_minute = separateTimeStamp(ccItem.start_time, "minute");
      const start_second = separateTimeStamp(ccItem.start_time, "second");
      const end_minute = separateTimeStamp(ccItem.end_time, "minute");
      const end_second = separateTimeStamp(ccItem.end_time, "second");
      const tl_text = ccItem.text;
      return { start_minute, start_second, end_minute, end_second, tl_text };
    });

    setValue("content.core.cc", ccArray);
    // storage(ccArray);
  };

  const getFile = (e) => {
    const file = e.target.files[0];

    if (!validateFile(file)) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      try {
        if (file.name.includes(".srt")) {
          readSRT(reader.result);
        }
        if (file.type === "application/json") {
          readJSON(reader.result);
        }
      } catch (err) {
        setFileError("Invalid file. Please upload a valid file.");
      }
    };
  };

  useEffect(() => {
      if (fileError) console.warn("File Error:", fileError);
    }, [fileError]);

  const renderTypeValue = (type_id) => {
    switch (type_id) {
      case 1:
        setValue("content.core.media.type", "video");
        break;
      case 2:
        setValue("content.core.media.type", "audio");
        break;
      default:
        setValue("content.core.media.type", "image");
        break;
    }
  };

  return (
    <div>

      <NumberedHeading number="1" title="Content Type" instructions="Select the type of content." />
      <Controller
        control={control}
        name="content.core.type_id"
        render={({ field }) => (
          <Controller
            control={control}
            name="content.core.type_id"
            render={({ field }) => (
              <FormControl fullWidth className="mt-16" error={!!errors.content?.core?.type_id}>
                <Select
                  {...field}
                  id="type_id"
                  classes={{ select: "flex items-center space-x-12" }}
                  displayEmpty
                  inputProps={{ "aria-label": "Content Type" }}
                  required
                >
                  <MenuItem value="" disabled className="space-x-12">
                    Select a content type
                  </MenuItem>
                  <MenuItem value={0} className="space-x-12">
                    eMentor Note
                  </MenuItem>
                  <MenuItem value={1} className="space-x-12">
                    Video
                  </MenuItem>
                  <MenuItem value={2} className="space-x-12">
                    Audio
                  </MenuItem>
                  <MenuItem value={3} className="space-x-12">
                    Image
                  </MenuItem>
                  <MenuItem value={4} className="space-x-12">
                    Image Question
                  </MenuItem>
                </Select>
                {!!errors.content?.core?.type_id && (
                  <FormHelperText>{errors.content?.core?.type_id?.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        )}
      />

      <Divider className="my-16" />

      {watch(`content.core.type_id`) === "" ? (
        <div className="w-100 mx-32 my-96">
          <div className="flex items-center justify-center">
            <BASESvgIcon size={24}>heroicons-outline:exclamation</BASESvgIcon>
            <Typography className="text-16 p-16">
              Please select a content type.
            </Typography>
          </div>
        </div>
      ) : (
        <>
          {(watch(`content.core.type_id`) === 0) && (
            <>
              <NumberedHeading number="2" title="eMentor Notes" instructions="Using the controls below, add any supplemental content to this page" />
              <Controller
                control={control}
                name="content.core.eMentor"
                render={({ field }) => <SuppEmentorArray {...{ control, errors }} />}
              />
            </>
          )}

          {(watch(`content.core.type_id`) !== 0) && (
            renderTypeValue(watch(`content.core.type_id`))
          )}

          {(watch(`content.core.type_id`) === 1 || watch(`content.core.type_id`) === 2) && (
            <>
              <NumberedHeading number="2" title="Media" instructions="Fill in the data for the Media." />
              <Controller 
                control={control}
                name="content.core.media"
                render={({ field }) => <MediaAttribution targetObj="content.core.media" inputDir={inputDir} errors={errors.content?.core?.media} {...{control }} />}
              />

              <Divider className="mt-16" />

              <NumberedHeading number="3" title="Media Time Stamp" instructions="Add the trimmed timestamp of the media." />
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Controller
                    control={control}
                    name="content.core.start_minute"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="Start Time (Minute)"
                        placeholder="Start Time (Minute)"
                        error={!!errors.content?.core?.start_minute}
                        helperText={errors.content?.core?.start_minute?.message}
                        fullWidth
                        required
                        type="number"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Controller
                    control={control}
                    name="content.core.start_second"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="Start Time (Second)"
                        placeholder="Start Time (Second)"
                        error={!!errors.content?.core?.start_second}
                        helperText={errors.content?.core?.start_second?.message}
                        fullWidth
                        required
                        type="number"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Controller
                    control={control}
                    name="content.core.end_minute"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="End Time (Minute)"
                        placeholder="End Time (Minute)"
                        error={!!errors.content?.core?.end_minute}
                        helperText={errors.content?.core?.end_minute?.message}
                        fullWidth
                        required
                        type="number"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Controller
                    control={control}
                    name="content.core.end_second"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="End Time (Second)"
                        placeholder="End Time (Second)"
                        error={!!errors.content?.core?.end_second}
                        helperText={errors.content?.core?.end_second?.message}
                        fullWidth
                        required
                        type="number"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {(watch(`content.core.type_id`) === 1) && (
            <>
              <Divider className="my-16" />

              <NumberedHeading
                number="6"
                title="Closed Caption File Upload"
                instructions="Upload a closed caption JSON or SRT file for the selected listening. Acceptable file types include .json and .srt."
              />

              <Button
                component="label"
                role={undefined}
                variant="contained"
                className="mt-16 mb-24"
                color="secondary"
                tabIndex={0}
                startIcon={<BASESvgIcon>heroicons-solid:upload</BASESvgIcon>}
              >
                Upload
                <VisuallyHiddenInput
                  type="file"
                  onChange={(e) => getFile(e)}
                  accept=".json, .srt"
                />
              </Button>

              <NumberedHeading
                number="7"
                title="Closed Captions"
                instructions="Using the controls below, please add closed captions for the selected listening"
              />
              <Controller
                control={control}
                name="content.core.cc"
                render={({ field }) => (
                  <SuppCCArray inputDir={inputDir} {...{ control, errors }} />
                )}
              />
            </>
          )}


          {(watch(`content.core.type_id`) === 3 || watch(`content.core.type_id`) === 4) && (
            <>
              <NumberedHeading number="2" title="Image" instructions="Fill in the data for the Image." />
              <Controller 
                control={control}
                name="content.core.media"
                render={({ field }) => <ImageAttribution targetObj="content.core.media" inputDir={inputDir} errors={errors.content?.core?.media} {...{control }} />}
              />
            </>
          )}

          {(watch(`content.core.type_id`) !== 0 && watch(`content.core.type_id`) !== 4) && (
            <>

              {(watch(`content.core.type_id`) === 3 ? (
                <NumberedHeading number="3" title="Context" instructions="Enter some contextual text to support the media." />
              ) : (
                <NumberedHeading number="4" title="Context" instructions="Enter some contextual text to support the media." />
              ))}
              <Controller
                control={control}
                name="content.core.context"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Text"
                      placeholder="Text"
                      error={!!errors.content?.core?.context}
                      helperText={errors.content?.core?.context?.message}
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={10}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              />
            </>
          )}

          {(watch(`content.core.type_id`) === 4) && (
            <>
              <NumberedHeading number="3" title="Prompt" instructions='Enter a prompt in the target language.' />
              <Controller
                control={control}
                name="content.core.prompt"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Text"
                      placeholder="Text"
                      error={!!errors.content?.core?.prompt}
                      helperText={errors.content?.core?.prompt?.message}
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={10}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              />

              <Divider className="mt-16" />

              <NumberedHeading number="4" title="Lures" instructions='Using the controls below, please add lures linked to this prompt.' />
              <SuppMCLureArray inputDir={inputDir} {...{ control, errors }} />

              <Divider className="mt-16" />

              <NumberedHeading number="5" title="Correct Feedback" instructions="Enter the feedback for a correct response" />
              <Controller
                control={control}
                name="content.core.correct_feedback"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Text"
                      placeholder="Text"
                      error={!!errors.content?.core?.correct_feedback}
                      helperText={errors.content?.core?.correct_feedback?.message}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              /> 
              
              <Divider className="mt-16" />

              <NumberedHeading number="6" title="First Incorrect Feedback" instructions="Enter the feedback for the first incorrect response." required />
              <Controller
                control={control}
                name="content.core.incorrect_feedback_1"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Feedback"
                      placeholder="Feedback"
                      error={!!errors.content?.core?.incorrect_feedback_1}
                      helperText={errors.content?.core?.incorrect_feedback_1?.message}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              />

              <Divider className="mt-16" />

              <NumberedHeading number="7" title="Second Incorrect Feedback" instructions="Enter the feedback for the second incorrect response." required />
              <Controller
                control={control}
                name="content.core.incorrect_feedback_2"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Feedback"
                      placeholder="Feedback"
                      error={!!errors.content?.core?.incorrect_feedback_2}
                      helperText={errors.content?.core?.incorrect_feedback_2?.message}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              />

              <Divider className="mt-16" />

              <NumberedHeading number="8" title="Third Incorrect Feedback" instructions="Enter the feedback for the third incorrect response." required />
              <Controller
                control={control}
                name="content.core.incorrect_feedback_3"
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      className="mt-16 mb-16"
                      label="Feedback"
                      placeholder="Feedback"
                      error={!!errors.content?.core?.incorrect_feedback_3}
                      helperText={errors.content?.core?.incorrect_feedback_3?.message}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      required
                      dir={inputDir}
                    />
                  </>
                )}
              />
              
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PESupplementalContent;